import React, { useContext, useEffect, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { ChevronRight } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import {
	ExportStatusEnum,
	JOIN_USER_EVENTS_ROOM,
	ON_USER_DATA_EXPORT,
} from '@sportspredict/shared';
import { useExportUserDataMutation, useGetUserExportFileUrlQuery } from '@/store/api';
import { promptDownloadFile } from '@/utils';
import { SocketContext } from '../contexts/socket.context';
import { toast } from '@/components/ui/use-toast';
import { useSocketRoom } from '@/hooks';

interface Props {
	user: { id: string } | null | undefined;
	open: boolean;
}

export default function ExportDataButton({ user, open }: Props) {
	const [exportedFileId, setExportedFileId] = useState<string | null>(null);
	const socket = useContext(SocketContext) as Socket | null;

	const [exportUserData] = useExportUserDataMutation();
	const { data: signedLinkData } = useGetUserExportFileUrlQuery(exportedFileId ?? skipToken);

	useEffect(() => {
		if (!signedLinkData || !exportedFileId) return;

		toast({
			title: 'Your file is ready',
			description: 'Tap to download your data.',
			duration: 60000,
			action: (
				<button
					type="button"
					onClick={() => promptDownloadFile(signedLinkData.url, signedLinkData.fileId)}
					className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-white/40 bg-transparent px-3 text-sm font-medium transition-colors hover:bg-white/10"
				>
					Download
				</button>
			),
		});

		setExportedFileId(null);
	}, [signedLinkData, exportedFileId]);

	useSocketRoom({
		socket,
		enabled: open,
		eventName: ON_USER_DATA_EXPORT,
		handler: (data: { status: ExportStatusEnum; fileId?: string }) => {
			if (data?.status === ExportStatusEnum.COMPLETED && data?.fileId) {
				setExportedFileId(data.fileId);
			}
		},
		join: () => socket?.emit(JOIN_USER_EVENTS_ROOM, { userId: user?.id }),
	});

	const handleClick = async () => {
		try {
			await exportUserData(undefined).unwrap();
			toast({
				title: 'Your file is being prepared',
				description: "We'll let you know when it's ready to download.",
			});
		} catch {
			toast({
				title: 'Something went wrong',
				description: 'Could not start the data export.',
				variant: 'destructive',
			});
		}
	};

	return (
		<button
			onClick={handleClick}
			className="
        w-full h-14
        px-6
        flex items-center justify-between
        bg-transparent
        text-[13px] text-[#F7F7F8]
        hover:bg-white/5
      "
		>
			<span className="text-sm font-normal">Download My Data</span>
			<ChevronRight className="w-5 h-5 text-[#D4D5D8]" />
		</button>
	);
}
